/**
 * mechatOutbox.ts — local log of messages sent from THIS device.
 *
 * A sent message cannot be recovered from the chain: it is encrypted to the
 * recipient's key and the ephemeral private key is destroyed right after
 * encryption. So the only way to show our own side of a conversation is to keep
 * the plaintext locally at send time — which is exactly what the PayCash wallet
 * does, and why sent messages appear only on the device that sent them.
 *
 * Storage is per account, in localStorage. Nothing is synced anywhere.
 */

const PREFIX = 'mechat.outbox.'
/** Cap per account so the log cannot grow without bound. */
const MAX_ENTRIES = 500

export interface OutboxEntry {
  /** Transaction id, or '' while the transaction is still in flight. */
  txid: string
  to: string
  text: string
  /** ms epoch, set locally at send time. */
  sentAt: number
  /** Local id so an entry can be updated once the txid is known. */
  localId: string
}

function keyFor(account: string): string {
  return `${PREFIX}${account}`
}

export function loadOutbox(account: string): OutboxEntry[] {
  if (!account || typeof localStorage === 'undefined') return []
  try {
    const raw = localStorage.getItem(keyFor(account))
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (e): e is OutboxEntry =>
        typeof e === 'object' && e !== null && typeof (e as OutboxEntry).text === 'string',
    )
  } catch {
    return []
  }
}

function save(account: string, entries: OutboxEntry[]): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(keyFor(account), JSON.stringify(entries.slice(-MAX_ENTRIES)))
  } catch {
    /* quota — drop silently, the chain copy still exists */
  }
}

export function appendOutbox(account: string, entry: OutboxEntry): OutboxEntry[] {
  const next = [...loadOutbox(account), entry]
  save(account, next)
  return next
}

/** Replaces an entry's text (e.g. a file attachment's `<file>` tag once uploaded). */
export function setOutboxText(account: string, localId: string, text: string): OutboxEntry[] {
  const next = loadOutbox(account).map(e => (e.localId === localId ? { ...e, text } : e))
  save(account, next)
  return next
}

/** Fills in the txid once the transaction is confirmed. */
export function attachTxid(account: string, localId: string, txid: string): OutboxEntry[] {
  const next = loadOutbox(account).map(e => (e.localId === localId ? { ...e, txid } : e))
  save(account, next)
  return next
}

export function removeFromOutbox(account: string, localId: string): OutboxEntry[] {
  const next = loadOutbox(account).filter(e => e.localId !== localId)
  save(account, next)
  return next
}

export function outboxByTxid(entries: OutboxEntry[]): Map<string, OutboxEntry> {
  const map = new Map<string, OutboxEntry>()
  for (const e of entries) if (e.txid) map.set(e.txid, e)
  return map
}

export function newLocalId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}
